/**
 * Internal (feat-125, Slice B / Part 1) — Bespoke-staff-only admin home.
 *
 * Relocates the two admin-only internal tools (Permissions, POS Matching
 * Inspector) off the (Admin/Manager/Corporate-visible) Administration page onto
 * this admin-only page, and adds the new Point-in-Time Timeline.
 *
 * The route is wrapped in <AdminRoute> (App.tsx) and the nav entry is gated on
 * isAdmin (ScorecardNavigation.tsx) — this component does not re-gate. Styling
 * mirrors Administration.tsx's tile grid.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ScorecardNavigation from './ScorecardNavigation';

interface InternalTile {
  title: string;
  description: string;
  path: string;
  emoji: string;
}

const TILES: InternalTile[] = [
  {
    // feat-289. FIRST in the grid because it is the only tile with a deadline:
    // an unreviewed line is not served to a phone (AC8), so a draft still
    // sitting here on Monday evening means that server sees the fallback card
    // for the whole week.
    //
    // This hub is the right home for it and the ONLY one. /crew-callouts is
    // <AdminRoute>, this page is <AdminRoute>, and the nav entry that reaches
    // here is gated on isAdmin — so linking it from here adds no surface a
    // manager or corporate user can see. AC24: no manager, corporate or
    // customer-facing surface renders a delivered line. Do not add this link to
    // Administration.tsx, which is Admin/Manager/Corporate-visible.
    title: 'Crew call-outs',
    description:
      'Weekly review queue: approve, edit or reject each machine-written praise line before it reaches an employee’s phone.',
    path: '/crew-callouts',
    emoji: '📣',
  },
  {
    // feat-318-admin-ui-conveniences: moved here from the avatar dropdown. The
    // route itself stays <StaffRoute>; only the nav entry is admin-only now.
    title: 'Tenant Management',
    description: 'Create and configure tenants, locations, corporate managers and client status.',
    path: '/tenant_management',
    emoji: '🏢',
  },
  {
    title: 'Permissions',
    description: 'Read-only reference of which features each role can access.',
    path: '/restaurant/administration/permissions',
    emoji: '🔐',
  },
  {
    title: 'POS Matching Inspector',
    description: 'Read-only diagnostic: inspect the full POS order → shift → interaction → conversion matching chain.',
    path: '/restaurant/administration/pos-inspector',
    emoji: '🔎',
  },
  {
    title: 'Point-in-Time Timeline',
    description: 'See every recording server, the AI detections, and the customer’s order on one shared restaurant-local clock.',
    path: '/restaurant/internal/timeline',
    emoji: '⏱️',
  },
  {
    title: 'Shift Interaction Evaluations',
    description: 'QA dashboard: verification coverage, AI accuracy, and a worklist of unverified shifts to review.',
    path: '/restaurant/internal/interaction-evaluations',
    emoji: '✅',
  },
  {
    title: 'Pilot Trends',
    description: 'Benchmark → pilot upsell metrics over time — test vs control, four trend charts with phase bands.',
    path: '/restaurant/internal/pilot-trends',
    emoji: '📈',
  },
  {
    // feat-342. DB-backed (not the per-ECS-task live board), so it sees every
    // phone. Admin-only like every tile on this page.
    title: 'Recording Activity',
    description:
      'Is every phone actually recording? Latest shift per person, from the database — not just this server’s live sockets.',
    path: '/restaurant/internal/recording-activity',
    emoji: '🎙️',
  },
  {
    title: 'Pairing Challenge',
    description: "Edit the head-to-head pairing card's daily goals, pace, and numbers for both locations.",
    path: '/admin/pairing-challenge',
    emoji: '⚔️',
  },
];

const InternalPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-4xl font-display font-semibold text-ink">Bespoke Admin</h1>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-coral bg-coral-light/50 border border-coral/40 rounded-full px-2 py-0.5">
              🔒 Bespoke staff only
            </span>
          </div>
          <p className="text-sm text-inkMuted">Internal diagnostic tools for Bespoke staff. Not visible to tenant users.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map((tile) => (
            <button
              key={tile.path}
              onClick={() => navigate(tile.path)}
              className="text-left bg-white border border-softBorder rounded-xl p-5 shadow-sm hover:shadow-md hover:border-coral/40 transition-all"
            >
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-coral to-gold flex items-center justify-center text-2xl mb-3">
                {tile.emoji}
              </div>
              <h2 className="text-lg font-display font-semibold text-ink mb-1">{tile.title}</h2>
              <p className="text-sm text-inkMuted">{tile.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InternalPage;
